import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api, errMsg } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function SignUpPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", organization_name: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password needs at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/auth/signup", form);
      navigate("/check-email", {
        state: { email: form.email, devVerifyUrl: res.data.dev_verify_url, emailSent: res.data.email_sent },
      });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <Card className="border shadow-none">
        <CardHeader>
          <CardTitle className="text-xl">Create your organization</CardTitle>
          <CardDescription>Set up BIIPP for your team in under a minute.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Your name</Label>
              <Input id="name" required maxLength={100} value={form.name} onChange={set("name")}
                placeholder="Alex Rivera" data-testid="signup-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org">Organization name</Label>
              <Input id="org" required maxLength={100} value={form.organization_name} onChange={set("organization_name")}
                placeholder="Acme Logistics" data-testid="signup-org-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Work email</Label>
              <Input id="email" type="email" autoComplete="email" required value={form.email} onChange={set("email")}
                placeholder="you@company.com" data-testid="signup-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete="new-password" required minLength={8} maxLength={72}
                value={form.password} onChange={set("password")} placeholder="At least 8 characters"
                data-testid="signup-password-input" />
            </div>
            <Button type="submit" className="w-full rounded-xl" disabled={busy} data-testid="signup-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
            </Button>
            <p className="text-xs text-muted-foreground leading-relaxed">
              We'll send a verification link to your email. You'll need to confirm it before logging in.
            </p>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-foreground underline underline-offset-4" data-testid="signup-login-link">
              Log in
            </Link>
          </p>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
